import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, Search, Users, UserCheck, CalendarCheck, CreditCard, Clock3, CheckCircle2, Banknote, ArrowLeft, Tag, Save, Building2, Sparkles, X, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, streamPost } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { LOGO_URL } from "@/components/Footer";
import MembersTab from "@/components/admin/MembersTab";
import AttendanceTab from "@/components/admin/AttendanceTab";
import PaymentsTab from "@/components/admin/PaymentsTab";

const STATUS_STYLES = {
  pending: "border-yellow-500/40 bg-yellow-500/10 text-yellow-400",
  confirmed: "border-blue-500/40 bg-blue-500/10 text-blue-400",
  paid: "border-volt/40 bg-volt/10 text-volt",
};

const Admin = () => {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [admissions, setAdmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [packages, setPackages] = useState([]);
  const [offerForm, setOfferForm] = useState(null);
  const [savingOffer, setSavingOffer] = useState(false);
  const [view, setView] = useState("admissions");
  const [franchises, setFranchises] = useState([]);
  const [loadingFranchise, setLoadingFranchise] = useState(false);
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpLoading, setCpLoading] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    setCpLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cpCurrent, new_password: cpNew });
      toast.success("Password changed successfully");
      setCpCurrent("");
      setCpNew("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCpLoading(false);
    }
  };

  const generateSummary = () => {
    if (summaryLoading) return;
    setSummary("");
    setShowSummary(true);
    setSummaryLoading(true);
    streamPost("/ai/summary", {}, {
      onDelta: (d) => setSummary((s) => s + d),
      onDone: () => setSummaryLoading(false),
      onError: () => { setSummary("Summary unavailable right now."); setSummaryLoading(false); },
    });
  };

  const FRANCHISE_STATUS_STYLES = {
    new: "border-yellow-500/40 bg-yellow-500/10 text-yellow-400",
    contacted: "border-blue-500/40 bg-blue-500/10 text-blue-400",
    closed: "border-zinc-600 bg-zinc-800/40 text-zinc-400",
  };
  const BUDGET_LABELS = { "under-10": "Under ₹10L", "10-20": "₹10–20L", "20-40": "₹20–40L", "40-plus": "₹40+L" };

  const loadFranchises = async () => {
    setLoadingFranchise(true);
    try {
      const { data } = await api.get("/franchise-enquiries");
      setFranchises(data);
    } catch {
      toast.error("Failed to load franchise enquiries");
    } finally {
      setLoadingFranchise(false);
    }
  };

  const updateFranchiseStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/franchise-enquiries/${id}`, { status });
      setFranchises((list) => list.map((f) => (f.id === id ? data : f)));
      toast.success(`Status updated to ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (user && view === "franchise") loadFranchises();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, user]);

  const loadData = useCallback(async (q = search, status = statusFilter) => {
    setLoadingList(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const [listRes, statsRes] = await Promise.all([
        api.get("/admissions", { params }),
        api.get("/admissions/stats"),
      ]);
      setAdmissions(listRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load admissions");
    } finally {
      setLoadingList(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) {
      loadData("", "");
      api.get("/packages").then((res) => setPackages(res.data)).catch(() => {});
      api.get("/offer").then((res) => { if (res.data && res.data.title_en) setOfferForm(res.data); }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveOffer = async (e) => {
    e.preventDefault();
    setSavingOffer(true);
    try {
      const payload = {
        ...offerForm,
        original_price: parseInt(offerForm.original_price, 10) || 0,
        offer_price: parseInt(offerForm.offer_price, 10) || 0,
      };
      const { data } = await api.put("/offer", payload);
      setOfferForm(data);
      toast.success("Offer updated — visitors will see the pop-up on their next visit");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingOffer(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("fitnastic_admin_token", data.access_token);
      setUser(data.user);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("fitnastic_admin_token");
    setUser(null);
  };

  const applyFilters = (q, status) => {
    setSearch(q);
    setStatusFilter(status);
    loadData(q, status);
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admissions/${id}`, { status });
      setAdmissions((list) => list.map((a) => (a.id === id ? data : a)));
      const statsRes = await api.get("/admissions/stats");
      setStats(statsRes.data);
      toast.success(`Status updated to ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-volt" />
      </div>
    );
  }

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-5" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md rounded-2xl border border-zinc-800 bg-panel p-8"
        >
          <div className="mb-8 flex justify-center">
            <img src={LOGO_URL} alt="Fitnastic Gym" className="w-52 rounded-lg" data-testid="admin-login-logo" />
          </div>
          <h1 className="text-center font-display text-2xl font-extrabold uppercase tracking-widest text-white" data-testid="admin-login-title">
            Admin Access
          </h1>
          <p className="mt-1 text-center text-sm text-zinc-500">Fitnastic Gym Management</p>
          <form onSubmit={login} className="mt-8 space-y-4" data-testid="admin-login-form">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@fitnastic.com"
              className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-3.5 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-volt focus:ring-1 focus:ring-volt"
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-3.5 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-volt focus:ring-1 focus:ring-volt"
            />
            {loginError && <p className="text-sm text-red-400" data-testid="admin-login-error">{loginError}</p>}
            <button
              data-testid="admin-login-submit-button"
              type="submit"
              disabled={loggingIn}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-volt py-3.5 font-display text-sm font-bold uppercase tracking-widest text-ink transition-all hover:bg-volt-hover disabled:opacity-60"
            >
              {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
              Login
            </button>
          </form>
          <Link to="/" data-testid="admin-back-home-link" className="mt-6 flex items-center justify-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-volt">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </Link>
        </motion.div>
      </div>
    );
  }

  const statCards = [
    { icon: Users, label: "Total", value: stats?.total ?? 0, testid: "stats-total" },
    { icon: Clock3, label: "Pending", value: stats?.pending ?? 0, testid: "stats-pending" },
    { icon: CheckCircle2, label: "Confirmed", value: stats?.confirmed ?? 0, testid: "stats-confirmed" },
    { icon: Banknote, label: "Paid", value: stats?.paid ?? 0, testid: "stats-paid" },
  ];

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-dashboard">
      <header className="border-b border-zinc-800 bg-surface/70">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <Logo />
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="dashboard-back-home-link" className="hidden font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-volt sm:block">
              View Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-zinc-700 px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:border-red-500 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight" data-testid="dashboard-title">
          Admissions <span className="text-volt">Dashboard</span>
        </h1>
        <p className="mt-1 text-sm text-zinc-500">Logged in as {user.email}</p>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="stats-grid">
          {statCards.map((s) => (
            <div key={s.testid} data-testid={s.testid} className="rounded-xl border border-zinc-800 bg-surface p-5">
              <s.icon className="mb-3 h-5 w-5 text-volt" />
              <p className="font-display text-4xl font-extrabold">{s.value}</p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-zinc-500">{s.label}</p>
            </div>
          ))}
        </div>

        {offerForm && (
          <form onSubmit={saveOffer} className="mt-8 rounded-xl border border-volt/30 bg-surface p-6" data-testid="offer-editor">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold uppercase tracking-wide" data-testid="offer-editor-title">
                <Tag className="h-5 w-5 text-volt" /> Offer <span className="text-volt">Pop-up</span>
              </h2>
              <label className="flex cursor-pointer items-center gap-2.5 font-mono text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
                <input
                  type="checkbox"
                  data-testid="offer-active-toggle"
                  checked={offerForm.active}
                  onChange={(e) => setOfferForm({ ...offerForm, active: e.target.checked })}
                  className="h-4 w-4 accent-[#FFE600]"
                />
                Pop-up Active
              </label>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500" htmlFor="of-title-en">Title (English) *</label>
                <input id="of-title-en" data-testid="offer-title-en-input" required className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-2.5 text-sm text-white outline-none focus:border-volt"
                  value={offerForm.title_en} onChange={(e) => setOfferForm({ ...offerForm, title_en: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500" htmlFor="of-title-bn">Title (বাংলা)</label>
                <input id="of-title-bn" data-testid="offer-title-bn-input" className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-2.5 text-sm text-white outline-none focus:border-volt"
                  value={offerForm.title_bn} onChange={(e) => setOfferForm({ ...offerForm, title_bn: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500" htmlFor="of-package">Linked Package</label>
                <select id="of-package" data-testid="offer-package-select" className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-2.5 text-sm text-white outline-none focus:border-volt"
                  value={offerForm.package_slug} onChange={(e) => setOfferForm({ ...offerForm, package_slug: e.target.value })}>
                  {packages.map((p) => (
                    <option key={p.slug} value={p.slug}>{p.name_en} — ₹{p.price.toLocaleString("en-IN")}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500" htmlFor="of-sub-en">Subtitle (English)</label>
                <input id="of-sub-en" data-testid="offer-subtitle-en-input" className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-2.5 text-sm text-white outline-none focus:border-volt"
                  value={offerForm.subtitle_en} onChange={(e) => setOfferForm({ ...offerForm, subtitle_en: e.target.value })} />
              </div>
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500" htmlFor="of-sub-bn">Subtitle (বাংলা)</label>
                <input id="of-sub-bn" data-testid="offer-subtitle-bn-input" className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-2.5 text-sm text-white outline-none focus:border-volt"
                  value={offerForm.subtitle_bn} onChange={(e) => setOfferForm({ ...offerForm, subtitle_bn: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500" htmlFor="of-badge-en">Badge (English)</label>
                <input id="of-badge-en" data-testid="offer-badge-en-input" className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-2.5 text-sm text-white outline-none focus:border-volt"
                  value={offerForm.badge_en} onChange={(e) => setOfferForm({ ...offerForm, badge_en: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500" htmlFor="of-badge-bn">Badge (বাংলা)</label>
                <input id="of-badge-bn" data-testid="offer-badge-bn-input" className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-2.5 text-sm text-white outline-none focus:border-volt"
                  value={offerForm.badge_bn} onChange={(e) => setOfferForm({ ...offerForm, badge_bn: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500" htmlFor="of-orig">Original Price (₹)</label>
                <input id="of-orig" data-testid="offer-original-price-input" type="number" min="0" className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-2.5 text-sm text-white outline-none focus:border-volt"
                  value={offerForm.original_price} onChange={(e) => setOfferForm({ ...offerForm, original_price: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500" htmlFor="of-price">Offer Price (₹) *</label>
                <input id="of-price" data-testid="offer-price-input" type="number" min="0" required className="w-full rounded-lg border border-volt/50 bg-ink px-4 py-2.5 text-sm font-bold text-volt outline-none focus:border-volt"
                  value={offerForm.offer_price} onChange={(e) => setOfferForm({ ...offerForm, offer_price: e.target.value })} />
              </div>
            </div>
            <button
              data-testid="offer-save-button"
              type="submit"
              disabled={savingOffer}
              className="mt-5 flex items-center gap-2 rounded-full bg-volt px-6 py-3 font-display text-sm font-bold uppercase tracking-widest text-ink transition-all hover:bg-volt-hover disabled:opacity-60"
            >
              {savingOffer ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Offer
            </button>
          </form>
        )}

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-view-tabs">
          <button
            data-testid="view-admissions-tab"
            onClick={() => setView("admissions")}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-colors ${
              view === "admissions" ? "border-volt bg-volt/10 text-volt" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            <Users className="h-4 w-4" /> Admissions
          </button>
          <button
            data-testid="view-members-tab"
            onClick={() => setView("members")}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-colors ${
              view === "members" ? "border-volt bg-volt/10 text-volt" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            <UserCheck className="h-4 w-4" /> Members
          </button>
          <button
            data-testid="view-attendance-tab"
            onClick={() => setView("attendance")}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-colors ${
              view === "attendance" ? "border-volt bg-volt/10 text-volt" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            <CalendarCheck className="h-4 w-4" /> Attendance
          </button>
          <button
            data-testid="view-payments-tab"
            onClick={() => setView("payments")}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-colors ${
              view === "payments" ? "border-volt bg-volt/10 text-volt" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            <CreditCard className="h-4 w-4" /> Payments
          </button>
          <button
            data-testid="view-franchise-tab"
            onClick={() => setView("franchise")}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-colors ${
              view === "franchise" ? "border-volt bg-volt/10 text-volt" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            <Building2 className="h-4 w-4" /> Franchise Enquiries
          </button>
          <button
            data-testid="ai-summary-button"
            onClick={generateSummary}
            className="flex items-center gap-2 rounded-full border border-volt/60 bg-volt/5 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-volt transition-colors hover:bg-volt hover:text-ink"
          >
            {summaryLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI Summary
          </button>
        </div>

        {showSummary && (
          <div className="mt-6 rounded-xl border border-volt/30 bg-surface p-6" data-testid="ai-summary-card">
            <div className="flex items-center justify-between gap-3">
              <h3 className="flex items-center gap-2 font-display text-lg font-bold uppercase tracking-widest text-white" data-testid="ai-summary-title">
                <Sparkles className="h-4 w-4 text-volt" /> AI Business Summary
              </h3>
              <button
                data-testid="ai-summary-close-button"
                onClick={() => setShowSummary(false)}
                className="rounded-full border border-zinc-700 p-1.5 text-zinc-400 hover:border-volt hover:text-volt"
                aria-label="Close summary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-zinc-300" data-testid="ai-summary-text">
              {summary || (summaryLoading ? "Analyzing admissions and enquiries..." : "")}
            </div>
          </div>
        )}

        {view === "members" && <MembersTab />}
        {view === "attendance" && <AttendanceTab />}
        {view === "payments" && <PaymentsTab />}

        {view === "admissions" && (
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
            <input
              data-testid="admission-search-input"
              value={search}
              onChange={(e) => applyFilters(e.target.value, statusFilter)}
              placeholder="Search by name or phone..."
              className="w-full rounded-full border border-zinc-700 bg-surface py-3 pl-11 pr-4 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-volt"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="status-filters">
            {["", "pending", "confirmed", "paid"].map((s) => (
              <button
                key={s || "all"}
                data-testid={`status-filter-${s || "all"}-button`}
                onClick={() => applyFilters(search, s)}
                className={`rounded-full border px-4 py-2 font-display text-xs font-bold uppercase tracking-widest transition-colors ${
                  statusFilter === s ? "border-volt bg-volt/10 text-volt" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                }`}
              >
                {s || "All"}
              </button>
            ))}
          </div>
        </div>

        )}

        {view === "admissions" && (
        <div className="mt-6 hidden overflow-x-auto rounded-xl border border-zinc-800 md:block" data-testid="admissions-table-wrapper">
          {loadingList ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-volt" /></div>
          ) : admissions.length === 0 ? (
            <p className="py-16 text-center text-sm text-zinc-500" data-testid="admissions-empty">No admissions found.</p>
          ) : (
            <table className="w-full min-w-[760px] text-left text-sm" data-testid="admissions-table">
              <thead>
                <tr className="border-b border-zinc-800 bg-surface font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                  <th className="px-5 py-4">Member</th>
                  <th className="px-5 py-4">Phone</th>
                  <th className="px-5 py-4">Package</th>
                  <th className="px-5 py-4">Slot</th>
                  <th className="px-5 py-4">Gender / DOB</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {admissions.map((a) => (
                  <tr key={a.id} className="border-b border-zinc-800/60 transition-colors hover:bg-surface/60" data-testid={`admission-row-${a.id}`}>
                    <td className="px-5 py-4 font-semibold text-white">{a.name}</td>
                    <td className="px-5 py-4 font-mono text-zinc-400">{a.phone}</td>
                    <td className="px-5 py-4">
                      <span className="text-zinc-200">{a.package_name_en}</span>
                      <span className="ml-1.5 text-xs text-zinc-500">₹{a.package_price.toLocaleString("en-IN")}</span>
                    </td>
                    <td className="px-5 py-4 capitalize text-zinc-400">{a.preferred_slot}</td>
                    <td className="px-5 py-4 capitalize text-zinc-400">{a.gender}{a.dob ? ` • ${a.dob}` : ""}</td>
                    <td className="px-5 py-4 text-xs text-zinc-500">{new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`admission-status-select-${a.id}`}
                        value={a.status}
                        onChange={(e) => updateStatus(a.id, e.target.value)}
                        className={`rounded-full border px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest outline-none ${STATUS_STYLES[a.status]} bg-transparent`}
                      >
                        <option value="pending" className="bg-panel">Pending</option>
                        <option value="confirmed" className="bg-panel">Confirmed</option>
                        <option value="paid" className="bg-panel">Paid</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}

        {view === "admissions" && (
          <div className="mt-6 space-y-3 md:hidden" data-testid="admissions-cards">
            {loadingList ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-volt" /></div>
            ) : admissions.length === 0 ? (
              <p className="rounded-xl border border-zinc-800 py-16 text-center text-sm text-zinc-500" data-testid="admissions-empty-mobile">No admissions found.</p>
            ) : (
              admissions.map((a) => (
                <div key={a.id} className="rounded-xl border border-zinc-800 bg-surface p-4" data-testid={`admission-card-${a.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-white">{a.name}</p>
                      <p className="font-mono text-xs text-zinc-400">{a.phone}</p>
                    </div>
                    <select
                      data-testid={`admission-status-select-mobile-${a.id}`}
                      value={a.status}
                      onChange={(e) => updateStatus(a.id, e.target.value)}
                      className={`shrink-0 rounded-full border px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest outline-none ${STATUS_STYLES[a.status]} bg-transparent`}
                    >
                      <option value="pending" className="bg-panel">Pending</option>
                      <option value="confirmed" className="bg-panel">Confirmed</option>
                      <option value="paid" className="bg-panel">Paid</option>
                    </select>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-zinc-400">
                    <span className="truncate">{a.package_name_en} • ₹{a.package_price.toLocaleString("en-IN")}</span>
                    <span className="capitalize">{a.preferred_slot} batch</span>
                    <span className="capitalize">{a.gender}{a.dob ? ` • ${a.dob}` : ""}</span>
                    <span>{new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {view === "franchise" && (
          <div className="mt-6 hidden overflow-x-auto rounded-xl border border-zinc-800 md:block" data-testid="franchise-table-wrapper">
            {loadingFranchise ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-volt" /></div>
            ) : franchises.length === 0 ? (
              <p className="py-16 text-center text-sm text-zinc-500" data-testid="franchise-empty">No franchise enquiries yet.</p>
            ) : (
              <table className="w-full min-w-[820px] text-left text-sm" data-testid="franchise-table">
                <thead>
                  <tr className="border-b border-zinc-800 bg-surface font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                    <th className="px-5 py-4">Name</th>
                    <th className="px-5 py-4">Phone</th>
                    <th className="px-5 py-4">City</th>
                    <th className="px-5 py-4">Budget</th>
                    <th className="px-5 py-4">Finance</th>
                    <th className="px-5 py-4">Message</th>
                    <th className="px-5 py-4">Date</th>
                    <th className="px-5 py-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {franchises.map((f) => (
                    <tr key={f.id} className="border-b border-zinc-800/60 transition-colors hover:bg-surface/60" data-testid={`franchise-row-${f.id}`}>
                      <td className="px-5 py-4 font-semibold text-white">{f.name}</td>
                      <td className="px-5 py-4 font-mono text-zinc-400">{f.phone}</td>
                      <td className="px-5 py-4 text-zinc-300">{f.city}</td>
                      <td className="px-5 py-4 text-zinc-300">{BUDGET_LABELS[f.budget] || f.budget}</td>
                      <td className="px-5 py-4" data-testid={`franchise-finance-${f.id}`}>
                        {f.needs_finance ? (
                          <span className="rounded-full border border-volt/40 bg-volt/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-widest text-volt">Yes</span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="max-w-[220px] truncate px-5 py-4 text-zinc-500" title={f.message}>{f.message || "—"}</td>
                      <td className="px-5 py-4 text-xs text-zinc-500">{new Date(f.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`franchise-status-select-${f.id}`}
                          value={f.status}
                          onChange={(e) => updateFranchiseStatus(f.id, e.target.value)}
                          className={`rounded-full border px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest outline-none ${FRANCHISE_STATUS_STYLES[f.status]} bg-transparent`}
                        >
                          <option value="new" className="bg-panel">New</option>
                          <option value="contacted" className="bg-panel">Contacted</option>
                          <option value="closed" className="bg-panel">Closed</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {view === "franchise" && (
          <div className="mt-6 space-y-3 md:hidden" data-testid="franchise-cards">
            {loadingFranchise ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-volt" /></div>
            ) : franchises.length === 0 ? (
              <p className="rounded-xl border border-zinc-800 py-16 text-center text-sm text-zinc-500" data-testid="franchise-empty-mobile">No franchise enquiries yet.</p>
            ) : (
              franchises.map((f) => (
                <div key={f.id} className="rounded-xl border border-zinc-800 bg-surface p-4" data-testid={`franchise-card-${f.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-white">{f.name}</p>
                      <p className="font-mono text-xs text-zinc-400">{f.phone}</p>
                    </div>
                    <select
                      data-testid={`franchise-status-select-mobile-${f.id}`}
                      value={f.status}
                      onChange={(e) => updateFranchiseStatus(f.id, e.target.value)}
                      className={`shrink-0 rounded-full border px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest outline-none ${FRANCHISE_STATUS_STYLES[f.status]} bg-transparent`}
                    >
                      <option value="new" className="bg-panel">New</option>
                      <option value="contacted" className="bg-panel">Contacted</option>
                      <option value="closed" className="bg-panel">Closed</option>
                    </select>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-zinc-400">
                    <span className="truncate">{f.city} • {BUDGET_LABELS[f.budget] || f.budget}</span>
                    <span>{f.needs_finance ? "Finance: Yes" : "Finance: —"}</span>
                    <span className="col-span-2 truncate">{f.message || "—"}</span>
                    <span>{new Date(f.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <form onSubmit={changePassword} className="mt-10 max-w-md rounded-xl border border-zinc-800 bg-surface p-6" data-testid="change-password-card">
          <h2 className="flex items-center gap-2 font-display text-xl font-extrabold uppercase tracking-wide" data-testid="change-password-title">
            <KeyRound className="h-5 w-5 text-volt" /> Change Password
          </h2>
          <div className="mt-4 space-y-3">
            <input
              data-testid="cp-current-input"
              type="password"
              required
              value={cpCurrent}
              onChange={(e) => setCpCurrent(e.target.value)}
              placeholder="Current password"
              className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-volt"
            />
            <input
              data-testid="cp-new-input"
              type="password"
              required
              minLength={8}
              value={cpNew}
              onChange={(e) => setCpNew(e.target.value)}
              placeholder="New password (min 8 characters)"
              className="w-full rounded-lg border border-zinc-700 bg-ink px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-volt"
            />
            <button
              data-testid="cp-submit-button"
              type="submit"
              disabled={cpLoading}
              className="flex items-center gap-2 rounded-full bg-volt px-6 py-3 font-display text-sm font-bold uppercase tracking-widest text-ink transition-all hover:bg-volt-hover disabled:opacity-60"
            >
              {cpLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Update Password
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default Admin;
